import { Component } from '@theme/component';

/**
 * Safely parse a JSON array from a data attribute. Returns an empty array for
 * missing or malformed input so tracking never throws.
 *
 * @param {string | undefined} raw
 * @returns {string[]}
 */
function parseList(raw) {
  if (!raw) return [];
  try {
    const value = JSON.parse(raw);
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
}

/**
 * Fires the `Amazon Button Click` event to Klaviyo when a retailer "Buy Now"
 * link inside this element is clicked.
 *
 * Uses Klaviyo onsite tracking (`klaviyo.track`, via the onsite `klaviyo.js`
 * the Klaviyo app embed injects) rather than a Shopify Custom Pixel. Onsite
 * tracking attaches the event to Klaviyo's cookie profile (`__kla_id`), so
 * anonymous clicks merge into the real profile when the visitor later
 * identifies — no manual profile identifier and no orphan/anonymous profiles
 * (the limitation that made the pixel `/client/events` approach unreliable).
 *
 * Wire up by adding `on:click="track"` to the buy-now `<a>`. Product context is
 * read from this element's data attributes; variant id and destination from the
 * clicked link.
 *
 * @extends {Component}
 */
class BuyNowTracker extends Component {
  /**
   * Fires the Klaviyo event for a buy-now link click.
   *
   * @param {MouseEvent} event - Click delegated from the buy-now link via `on:click`.
   */
  track(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const link = target.closest('a[data-buy-now-link]');
    if (!(link instanceof HTMLAnchorElement)) return;

    // Respect the shopper's tracking consent before emitting to Klaviyo.
    if (!this.isTrackingAllowed()) return;

    let destinationDomain = '';
    try {
      destinationDomain = new URL(link.href).hostname;
    } catch (error) {
      // Malformed href — still track, just without a parsed domain.
    }

    const properties = {
      destination_url: link.href,
      destination_domain: destinationDomain,
      button_text: (link.textContent || '').trim(),
      product_id: this.dataset.productId || '',
      product_title: this.dataset.productTitle || '',
      product_handle: this.dataset.productHandle || '',
      product_tags: parseList(this.dataset.productTags),
      product_collections: parseList(this.dataset.productCollections),
      variant_id: link.dataset.variantId || '',
      source_page_type: this.dataset.sourcePageType || '',
    };

    // Fire via Klaviyo onsite tracking. The queue-safe push form works whether
    // or not klaviyo.js has finished loading; identity is handled by klaviyo.js.
    try {
      window.klaviyo = window.klaviyo || [];
      window.klaviyo.push(['track', 'Amazon Button Click', properties]);
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Whether analytics tracking is permitted for this visitor, per the Shopify
   * Customer Privacy API. Follows Shopify's documented guard: if the API isn't
   * loaded, tracking may proceed; if it is, gate on the analytics consent flag
   * (falling back to the aggregate userCanBeTracked()). Klaviyo is a
   * third-party platform, so we must not emit when tracking is denied.
   * https://shopify.dev/docs/api/customer-privacy
   *
   * @returns {boolean}
   */
  isTrackingAllowed() {
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy) return true; // API not present -> tracking may proceed
    try {
      if (typeof privacy.analyticsProcessingAllowed === 'function') {
        return privacy.analyticsProcessingAllowed();
      }
      if (typeof privacy.userCanBeTracked === 'function') {
        return privacy.userCanBeTracked();
      }
    } catch (error) {
      return true;
    }
    return true;
  }
}

if (!customElements.get('buy-now-tracker')) {
  customElements.define('buy-now-tracker', BuyNowTracker);
}
